/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Ladda from 'ladda';
import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';

import showErrorMessage from '@/util/showErrorMessage';

import FileUploadModel from '../../fileupload/FileUploadModel';
import FileUploadCollection from '../../fileupload/FileUploadCollection';
import fileUploadConfig from '../../fileupload/fileUploadConfig';

import FilesCollectionView from './FilesCollectionView';
import './FileUploadView.css';
import tmplFileUploadView from './FileUploadView.hbs';

const FileUploadView = View.extend({
  template: tmplFileUploadView,

  ui: {
    inputFile: 'input[type="file"]',
    linkUpload: '.upload',
    spinner: '.spinner'
  },

  events: {
    'change @ui.inputFile': 'onSetFiles',
    'click @ui.linkUpload': 'triggerFileSelect'
  },

  regions: {
    filesCollection: '.files-collection'
  },

  options() {
    const newDefaults = Object.assign(
      {},
      {
        metadata: {},
        searchPath: undefined,
        isReadOnly: false,
        pathToFileId: undefined,
        allowMultipleFiles: false,
        uploadSingleLabel: 'Attach File...',
        uploadMultipleLabel: 'Attach Files...',
        showError: false,
        // uploadClass: this.model.get('uploadClass') || '',
        validator: undefined,
        onFileRemove: undefined,
        returnData: () => {}
      },
      fileUploadConfig
    );
    return newDefaults;
  },

  // childViewEvents: {
  //   'file:delete': 'onFileDelete'
  // },

  collectionEvents: {
    remove: 'resetSingleFileUpload',
    add: 'hideUploadForSingleFile',
    'remove upload:canceled': 'onFileRemove',
    'upload:start': 'onUploadStart',
    'upload:complete': 'onUploadComplete',
    'upload:error': 'onUploadError',
    'file:delete:start': 'onFileDeleteStart',
    'file:delete:end': 'onFileDeleteEnd',
    'file:delete:error': 'onFileDeleteError'
  },

  collectionTriggers: {
    'upload:start': 'upload:start',
    'upload:complete': 'upload:complete',
    'upload:error': 'upload:error',
    'file:delete:end': 'file:deleted'
  },

  templateContext() {
    const { btnClass, travelerNumber, uploadMultipleLabel } = this.options;
    return {
      btnClass,
      travelerNumber,
      uploadMultipleLabel
    };
  },

  initialize(options) {
    const { searchPath, metadata, downloadXml, validator, isReadOnly, displayAsterisk } = this.options;
    Object.assign(this.options, options);
    if (!this.collection) {
      this.collection = new FileUploadCollection();
    } else {
      this.collection.each((model) => {
        if (isReadOnly) {
          model.set('isReadOnly', true);
        }
        if (validator) {
          model.set('validator', validator);
        }
        if (displayAsterisk) {
          model.set('displayAsterisk', true);
        }
        model.set({
          isPreloaded: true,
          tmplSoapMessage: downloadXml,
          searchPath,
          metadata
        });
      });
    }

    // Added by DoBS
    this.listenTo(this.collection, 'add', this.onModelAdd);
    this.listenTo(this.collection, 'remove', this.onModelRemove);
    this.listenTo(this.collection, 'remove upload:canceled', this.onFileRemove);
  },

  onRender() {
    this.showChildView(
      'filesCollection',
      new FilesCollectionView({
        collection: this.collection
      })
    );
    if (!this.options.allowMultipleFiles) {
      this.$('input[type="file"]').removeAttr('multiple');
      this.$('.upload').text(this.options.uploadSingleLabel);
      if (this.collection.length > 0) {
        this.$('.upload').hide();
      }
    }
    if (this.options.isReadOnly) {
      this.$('.upload').hide();
    }
    this.spinner = Ladda.create(this.ui.spinner[0]);
  },

  // Method that is called whenever an item is added to the collection
  onModelAdd() {
    Radio.channel('order').trigger('file:added', this.collection);
    this.hideUploadForSingleFile();
    this.removeValidationErrors();
  },

  // Method that is called whenever an item is removed from the collection
  onModelRemove() {
    this.resetSingleFileUpload();
  },

  onFileRemove(removedFile) {
    if (_.isFunction(this.options.onFileRemove)) {
      this.options.onFileRemove(removedFile);
    }
  },

  onUploadStart() {
    this.spinner.start();
  },

  onUploadComplete(attachmentId) {
    this.trigger('upload:complete', attachmentId);
    this.options.metadata.attachmentId = attachmentId;
    this.spinner.stop();
  },

  onUploadError(e, jqXHR, textStatus, errorThrown, context) {
    if (e?.responseText.includes('already exists')) {
      this.showDuplicateFileNameError();
    } else {
      this.showUploadError();
    }

    this.collection.reset();
    this.getChildView('filesCollection').collection.reset();
    this.resetSingleFileUpload();
    this.spinner?.stop();
  },

  triggerFileSelect(e) {
    e.preventDefault();
    this.$('input[type="file"]').trigger('click');
  },

  onSetFiles(e) {
    e.preventDefault();
    const files = e.target.files || e.originalEvent.dataTransfer.files;
    const { sizeLimit, acceptedMimeTypes, failedAdd, validator, returnData } = this.options;

    _.each(files, (file) => {
      const failSizeLimit = sizeLimit && file.size > sizeLimit;
      const failMimeTypes = acceptedMimeTypes && acceptedMimeTypes.indexOf(file.type) < 0;
      if (failSizeLimit || failMimeTypes) {
        typeof failedAdd === 'function' &&
          failedAdd({
            file,
            failSizeLimit,
            failMimeTypes
          });
        return;
      }
      if (validator && _.isFunction(validator)) {
        if (validator(file)) {
          this.addFileUploadModel(file);
        } else {
          this.trigger('file:validation:failed');
        }
      } else {
        this.addFileUploadModel(file);
      }
    });
    returnData(files);
    this.el.getElementsByClassName('files')[0].reset();
    this.trigger('file:uploaded');
  },

  onFileDeleteStart() {
    this.spinner.start();
  },

  onFileDeleteEnd() {
    this.spinner.stop();
    if (this.options?.metadata?.fileId) this.options.metadata.fileId = null;
    if (this.options?.metadata?.attachmentId) this.options.metadata.attachmentId = null;
    this.trigger('file:deleted');
  },

  onFileDeleteError() {
    this.spinner.stop();
    this.trigger('file:delete:error');
    this.showDeleteError();
  },

  showDeleteError() {
    showErrorMessage({
      errorCode: 'deleteError',
      errorTitle: 'Delete Error',
      errorMessage: 'An error occurred while deleting this file. Please try again or contact an administrator.'
    });
  },

  showUploadError() {
    showErrorMessage({
      errorCode: 'uploadError',
      errorTitle: 'Upload Error',
      errorMessage: 'An error occurred while uploading this file. Please try again or contact an administrator.'
    });
  },

  showDuplicateFileNameError() {
    showErrorMessage({
      errorCode: 'documentExists',
      errorTitle: 'Duplicate File Name',
      errorMessage:
        'All uploaded documents must have a unique file name. Please ensure that a document with the same name has not been uploaded in other areas of the form.'
    });
  },

  resetSingleFileUpload() {
    const { allowMultipleFiles } = this.options;
    if (!allowMultipleFiles) {
      this.$('.upload').show();
    }
    this.trigger('file:removed');
  },

  hideUploadForSingleFile() {
    const { allowMultipleFiles } = this.options;
    if (!allowMultipleFiles) {
      this.$('.upload').hide();
    }
  },

  addExistingFiles(files) {
    const { isReadOnly, searchPath, metadata, downloadXml } = this.options;
    if (!this.collection) {
      this.collection = new FileUploadCollection();
    } else {
      files.forEach((file) => {
        if (isReadOnly) {
          file.isReadOnly = true;
        }
        file.isPreloaded = true;
        file.searchPath = searchPath;
        file.metadata = metadata;
        file.tmplSoapMessage = downloadXml;
        this.collection.add(file);
      });
    }
  },

  replaceFileName(defaultFileName, filename) {
    if (filename && !defaultFileName) return filename;
    if (defaultFileName && !filename) return defaultFileName;

    const extRegex = /\.[\w\d]+$/;
    const defaultName = defaultFileName.replace(extRegex, '');
    const ext = filename.match(extRegex);

    if (defaultName && ext) return `${defaultName}${ext}`;
    return null;
  },

  addFileUploadModel(file) {
    this.collection.add(
      new FileUploadModel({
        file1: file,
        fileName: this.replaceFileName(this.options.defaultFileName, file.name),
        // fileSize: file.size,
        fileType: file.type,
        showError: this.options.showError,
        metadata: this.options.metadata,
        searchPath: this.options.searchPath,
        pathToFileId: this.options.pathToFileId,
        tmplMethodXML: this.options.uploadXml,
        tmplSoapMessage: this.options.downloadXml,
        timeout: this.options.timeout || this.options.timeOut,
        timeOut: this.options.timeout || this.options.timeOut
      })
    );
  },

  validate(config) {
    const validationType = _.get(config, 'type', 'submit');
    if (validationType === 'submit' && this.options.isRequired) {
      this.removeValidationErrors();
      let hasOneSuccessfulFile = true;
      if (!this.collection.length) {
        hasOneSuccessfulFile = false;
      } else {
        hasOneSuccessfulFile = this.collection.some((model) => {
          return model.get('fileId');
        });
      }
      if (!hasOneSuccessfulFile) {
        const $formGroup = this.$el.closest('.form-group');
        $formGroup.addClass('has-error');
        this.$el.after('<span class="validation-error-container help-block">This value is required</span>');
        return Promise.reject();
      } else {
        return Promise.resolve();
      }
    } else {
      Promise.resolve();
    }
  },

  getFirstFileId() {
    if (!this.collection.length || !this.collection.at(0)) {
      return null;
    }
    if (!this.collection.at(0).get('fileId')) {
      return null;
    }
    return this.collection.at(0).get('fileId');
  },

  removeValidationErrors() {
    const $formGroup = this.$el.closest('.form-group');
    $formGroup.removeClass('has-error');
    $formGroup.find('.validation-error-container').remove();
  }
});

export default FileUploadView;
